import { useState, useEffect, useMemo } from "react";
import { Box, Paper, Stack, Text, LoadingOverlay } from "@mantine/core";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUserContext } from "@/context/UserContext";
import NdaTemplate from "@/pages/nda/NdaTemplate";

interface NdaPageProps {
  otherCompanyId: string;
  collaboration?: {
    id?: string;
    requesterCompanyId?: string;
    receiverCompanyId?: string;
    requesterSignedNDA?: boolean;
    receiverSignedNDA?: boolean;
  } | null;
  collaborationReadOnly?: boolean;
}

/** Read-only NDA viewer (embedded in collaboration workspace). Signing happens at payment. */
export default function NdaPage({
  otherCompanyId,
  collaboration: propCollaboration,
  collaborationReadOnly = false,
}: NdaPageProps) {
  const { userDetails, hasProfile } = useUserContext();
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [collaboration, setCollaboration] = useState(propCollaboration ?? null);
  const [otherCompanyData, setOtherCompanyData] = useState<{
    gtid?: string | null;
    headquartersCountry?: string | null;
  } | null>(null);
  const [company1Name, setCompany1Name] = useState("");
  const [company1Address, setCompany1Address] = useState("");
  const [company2Name, setCompany2Name] = useState("");
  const [company2Address, setCompany2Address] = useState("");

  const matchId = collaboration?.id;

  const currentUserCompany = useMemo((): "company1" | "company2" => {
    if (!collaboration || !userDetails?.company?.id) return "company1";
    return collaboration.requesterCompanyId === userDetails.company.id ? "company1" : "company2";
  }, [collaboration?.requesterCompanyId, userDetails?.company?.id]);

  useEffect(() => {
    if (propCollaboration) {
      setCollaboration(propCollaboration);
    }
  }, [propCollaboration]);

  useEffect(() => {
    const fetchNdaContext = async () => {
      if (hasProfile === null) return;
      if (!userDetails?.company?.id || !otherCompanyId) {
        setIsLoadingData(false);
        return;
      }

      setIsLoadingData(true);
      try {
        const ndaContext = await trpc.nda.getNdaContext.query({ companyId: otherCompanyId });
        setCollaboration(ndaContext.collaboration);
        setOtherCompanyData(ndaContext.otherCompany);

        const userRole = ndaContext.currentUserRole;
        if (userRole === "company1") {
          setCompany1Name(ndaContext.userCompany.gtid || "Company 1");
          setCompany1Address("Registered address on file");
          setCompany2Name(ndaContext.otherCompany.gtid || "Company 2");
          setCompany2Address("Registered address on file");
        } else {
          setCompany2Name(ndaContext.userCompany.gtid || "Company 2");
          setCompany2Address("Registered address on file");
          setCompany1Name(ndaContext.otherCompany.gtid || "Company 1");
          setCompany1Address("Registered address on file");
        }
      } catch (err) {
        console.error("Error fetching NDA context:", err);
        handleTrpcError(err);
      } finally {
        setIsLoadingData(false);
      }
    };

    void fetchNdaContext();
  }, [otherCompanyId, userDetails?.company?.id, hasProfile]);

  const bothPartiesAccepted =
    !!collaboration?.requesterSignedNDA && !!collaboration?.receiverSignedNDA;

  const statusMessage = (() => {
    if (!collaboration) return "No active collaboration found with this company.";
    if (collaborationReadOnly) {
      return "This collaboration is read-only. The NDA is shown for your records.";
    }
    if (bothPartiesAccepted) {
      return "Both parties accepted the NDA when completing payment. You can review the agreement below.";
    }
    return "NDA acceptance is recorded when each party completes payment with the NDA checkbox.";
  })();

  return (
    <Box>
      <Stack gap="xl">
        <Box pos="relative">
          <LoadingOverlay visible={isLoadingData} zIndex={100} overlayProps={{ radius: "sm", blur: 2 }} />

          <NdaTemplate
            company1Name={company1Name}
            company1Address={company1Address}
            company2Name={company2Name}
            company2Address={company2Address}
            matchId={matchId}
            company2Data={otherCompanyData}
            company1SignedInDb={!!collaboration?.requesterSignedNDA}
            company2SignedInDb={!!collaboration?.receiverSignedNDA}
            company1ShortName={company1Name}
            company2ShortName={company2Name}
            company1IncorporationLaw={
              currentUserCompany === "company1"
                ? userDetails?.company?.headquartersCountry || "___"
                : otherCompanyData?.headquartersCountry || "___"
            }
            company2IncorporationLaw={
              currentUserCompany === "company2"
                ? userDetails?.company?.headquartersCountry || "___"
                : otherCompanyData?.headquartersCountry || "___"
            }
            governingLaw="Singapore"
            platformName="GTsetu"
            platformUrl="https://gtsetu.com"
            currentUserCompany={currentUserCompany}
            showAllDetails
          />
        </Box>

        <Paper p="md" radius="md" withBorder>
          <Text size="sm" c="slate.6">
            {statusMessage}
          </Text>
        </Paper>
      </Stack>
    </Box>
  );
}
